export const CHAT_IMAGE_ACCEPT =
  'image/png,image/jpeg,image/webp,image/heic,image/heif,.png,.jpg,.jpeg,.webp,.heic,.heif';

export const MAX_CHAT_IMAGES = 4;
export const MAX_CHAT_IMAGE_BYTES = 10 * 1024 * 1024;

const MAX_CHAT_IMAGE_EDGE = 1568;
const CHAT_IMAGE_JPEG_QUALITY = 0.85;

const ACCEPTED_CHAT_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const ACCEPTED_CHAT_IMAGE_EXTENSION = /\.(png|jpe?g|webp|heic|heif)$/i;
const HEIC_IMAGE_EXTENSION = /\.(heic|heif)$/i;

export type ChatImageTranscodeErrorCode =
  'unsupported_type' | 'decode_failed' | 'encode_failed';

export class ChatImageTranscodeError extends Error {
  code: ChatImageTranscodeErrorCode;

  constructor(message: string, code: ChatImageTranscodeErrorCode) {
    super(message);
    this.name = 'ChatImageTranscodeError';
    this.code = code;
  }
}

export const isAcceptedChatImageType = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();

  return (
    ACCEPTED_CHAT_IMAGE_TYPES.has(mimeType) ||
    (!mimeType && ACCEPTED_CHAT_IMAGE_EXTENSION.test(file.name))
  );
};

const isHeicImage = (file: File): boolean =>
  file.type.toLowerCase() === 'image/heic' ||
  file.type.toLowerCase() === 'image/heif' ||
  HEIC_IMAGE_EXTENSION.test(file.name);

const decodeImage = (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new window.Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);

      reject(
        new ChatImageTranscodeError(
          'Ora could not read this image. Try another file.',
          'decode_failed'
        )
      );
    };

    image.src = objectUrl;
  });

const encodeCanvasAsJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(
          new ChatImageTranscodeError(
            'Ora could not prepare this image. Try another file.',
            'encode_failed'
          )
        );
      },
      'image/jpeg',
      CHAT_IMAGE_JPEG_QUALITY
    );
  });

export const transcodeChatImage = async (
  file: File
): Promise<{ blob: Blob; mimeType: string; sizeBytes: number }> => {
  if (!isAcceptedChatImageType(file)) {
    throw new ChatImageTranscodeError(
      'Please attach a PNG, JPEG, WebP, HEIC, or HEIF image.',
      'unsupported_type'
    );
  }

  let sourceBlob: Blob = file;

  if (isHeicImage(file)) {
    try {
      const { default: heic2any } = await import('heic2any');

      const decoded = await heic2any({
        blob: file,
        quality: CHAT_IMAGE_JPEG_QUALITY,
        toType: 'image/jpeg',
      });

      const decodedBlob = Array.isArray(decoded) ? decoded[0] : decoded;

      if (!decodedBlob) {
        throw new Error('HEIC decoder returned no image');
      }

      sourceBlob = decodedBlob;
    } catch (error) {
      if (error instanceof ChatImageTranscodeError) throw error;

      throw new ChatImageTranscodeError(
        'Ora could not read this HEIC image. Try another file.',
        'decode_failed'
      );
    }
  }

  const image = await decodeImage(sourceBlob);
  const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);

  if (!longestEdge) {
    throw new ChatImageTranscodeError(
      'Ora could not read this image. Try another file.',
      'decode_failed'
    );
  }

  const scale = Math.min(1, MAX_CHAT_IMAGE_EDGE / longestEdge);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new ChatImageTranscodeError(
      'Ora could not prepare this image. Try another file.',
      'encode_failed'
    );
  }

  context.drawImage(image, 0, 0, width, height);

  const blob = await encodeCanvasAsJpeg(canvas);

  return {
    blob,
    mimeType: 'image/jpeg',
    sizeBytes: blob.size,
  };
};
